import { ItemCacheFunctions, addCachedFunctions, getCachedFunctions } from './CachedFunctions';
import { getItems } from '../query/getItems';
import { getItemsForLock } from '../query/getItemsForLock';
import { getItemIds } from '../query/getItemIds';
import { selectItemById } from '../query/selectItemById';
import { selectItemByIdForLock } from '../query/selectItemByIdForLock';
import {
    addItem,
    updateItem,
    upsertItemsForDescriptor,
    deleteItem,
    replaceItems,
} from '../actions/internalActions';
import { CacheItem } from './schema/CacheItem';
import { CacheDescriptor } from './schema/CacheDescriptor';
import { ItemCacheLockId } from './schema/ItemCacheLockId';
import { getStore, createItemCacheStore } from './store';
import { ItemsCacheApi } from './schema/ItemsCacheApi';
import { getDescriptors } from '../query/getDescriptors';

interface CacheInfo<T extends CacheItem, U extends CacheDescriptor> {
    cacheId: string;
    functions: ItemCacheFunctions<T, U>;
}

export function createItemCache<T extends CacheItem, U extends CacheDescriptor>(
    cacheInfo: CacheInfo<T, U>
): ItemsCacheApi<T, U> {
    let store = getStore<T, U>(cacheInfo.cacheId);
    let cacheFunctions = getCachedFunctions<T, U>(cacheInfo.cacheId);
    if (!cacheFunctions || !store) {
        // create store
        store = createItemCacheStore<T, U>(cacheInfo.cacheId);
        // add to saved functions
        cacheFunctions = addCachedFunctions(cacheInfo.cacheId, cacheInfo.functions);
    }
    const key = cacheInfo.cacheId;
    const { buildItemDescriptorFilter, containsDescriptor } = cacheFunctions;

    return {
        //  Get
        getItems: (descriptor: U) =>
            getItems<T, U>(store, descriptor, buildItemDescriptorFilter, containsDescriptor),
        getDescriptors: () => getDescriptors<T, U>(store),
        getItemsForLock: (lockId: ItemCacheLockId, descriptor: U) =>
            getItemsForLock<T, U>(
                store,
                lockId,
                descriptor,
                buildItemDescriptorFilter,
                containsDescriptor
            ),
        getItemIds: (descriptor: U) =>
            getItemIds(store, descriptor, buildItemDescriptorFilter, containsDescriptor),
        selectItemById: (id: string) => selectItemById(store, id),
        selectItemByIdForLock: (lockId: ItemCacheLockId, id: string) =>
            selectItemByIdForLock(store, lockId, id),

        // Update
        upsertItemsForDescriptor: (lockId: ItemCacheLockId, items: T[], descriptor: U) =>
            upsertItemsForDescriptor(key, lockId, items, descriptor),
        replaceItems: (items: T[]) => replaceItems(key, items),
        updateItem: (id: string, updates: Partial<T>) => updateItem(key, id, updates),
        addItem: (item: T) => addItem(key, item),
        deleteItem: (id: string) => deleteItem(key, id),
    };
}
